import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Dropdown } from "antd";
import {
  deleteSecureFile,
  getSecureFileAccessLogs,
  listSecureFiles,
  regenerateSecureFileShare,
  revokeSecureFileShare,
  uploadSecureFile,
} from "../../../../services/secureFiles";
import SecureFileSearchBar from "../../../Shared/SecureFileSearchBar";
import SecureFileTableHead from "../../../Shared/SecureFileTableHead";
import {
  DEFAULT_SECURE_FILE_SORT,
  filterSecureFiles,
  formatUploadedAt,
  formatRetentionExpiresAt,
  sortSecureFiles,
  toggleSecureFileSort,
} from "../../../../utils/secureFileList";
import "./styles.css";

import { displayDateTime } from "../../../../utils/dateDisplay";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const UPLOAD_STATUS = {
  PENDING: "pending",
  UPLOADING: "uploading",
  SUCCESS: "success",
  ERROR: "error",
};

const formatFileSize = (bytes) => {
  if (!bytes) {
    return "0 B";
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getShareStatus = (file) => {
  if (file.shareRevoked) {
    return {
      label: "Revoked",
      className: "tag is-danger",
      linkDetail: "Share link revoked",
    };
  }
  if (!file.shareActive) {
    return {
      label: "Expired",
      className: "tag is-warning",
      linkDetail: `Link expired ${moment(file.shareExpiresAt).format("DD/MM/YYYY")}`,
    };
  }
  return {
    label: "Active",
    className: "tag is-success",
    linkDetail: `Link expires ${moment(file.shareExpiresAt).format("DD/MM/YYYY")}`,
  };
};

const createUploadQueueItem = (file) => ({
  id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  file,
  status: UPLOAD_STATUS.PENDING,
  progress: 0,
  errorMessage: "",
});

const SecureFileTab = ({ clientAppointmentId }) => {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedAccessLogs, setSelectedAccessLogs] = useState(null);
  const [isAccessLogsLoading, setIsAccessLogsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState(DEFAULT_SECURE_FILE_SORT);

  const isUploading = uploadQueue.some(
    (item) => item.status === UPLOAD_STATUS.PENDING || item.status === UPLOAD_STATUS.UPLOADING
  );

  const loadFiles = useCallback(async () => {
    if (!clientAppointmentId) {
      return;
    }

    setIsLoading(true);
    setError("");
    const { data, error: listError } = await listSecureFiles(clientAppointmentId);
    if (listError) {
      setError(listError.message);
      setFiles([]);
    } else {
      setFiles(data || []);
    }
    setIsLoading(false);
  }, [clientAppointmentId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  useEffect(() => {
    setSearchQuery("");
    setSortConfig(DEFAULT_SECURE_FILE_SORT);
    setUploadQueue([]);
  }, [clientAppointmentId]);

  const filteredFiles = filterSecureFiles(files, searchQuery, getShareStatus);
  const displayedFiles = useMemo(() => sortSecureFiles(filteredFiles, sortConfig), [filteredFiles, sortConfig]);

  const handleSort = (column) => {
    setSortConfig((currentSort) => toggleSecureFileSort(currentSort, column));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const updateQueueItem = useCallback((itemId, updates) => {
    setUploadQueue((current) => current.map((item) => (item.id === itemId ? { ...item, ...updates } : item)));
  }, []);

  const uploadQueueItem = useCallback(
    async (queueItem) => {
      const { id, file } = queueItem;

      if (file.size > MAX_FILE_SIZE_BYTES) {
        updateQueueItem(id, {
          status: UPLOAD_STATUS.ERROR,
          progress: 0,
          errorMessage: "File exceeds the 50MB limit.",
        });
        return false;
      }

      updateQueueItem(id, { status: UPLOAD_STATUS.UPLOADING, progress: 0, errorMessage: "" });

      const { data, error: uploadError } = await uploadSecureFile({
        clientAppointmentId,
        file,
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total ? Math.round((progressEvent.loaded * 100) / progressEvent.total) : 0;
          updateQueueItem(id, { progress });
        },
      });

      if (uploadError) {
        updateQueueItem(id, {
          status: UPLOAD_STATUS.ERROR,
          progress: 0,
          errorMessage: uploadError.message,
        });
        return false;
      }

      updateQueueItem(id, {
        status: UPLOAD_STATUS.SUCCESS,
        progress: 100,
        errorMessage: "",
        uploadedFileName: data?.fileName,
      });
      return true;
    },
    [clientAppointmentId, updateQueueItem]
  );

  const handleFilesSelected = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selectedFiles.length) {
      return;
    }

    setError("");
    setSuccessMessage("");

    const newItems = selectedFiles.map(createUploadQueueItem);
    setUploadQueue((current) => [...current, ...newItems]);

    const results = await Promise.all(newItems.map((item) => uploadQueueItem(item)));
    const successCount = results.filter(Boolean).length;

    if (successCount > 0) {
      await loadFiles();
      setSuccessMessage(
        successCount === 1 ? "1 file uploaded successfully." : `${successCount} files uploaded successfully.`
      );
    }
  };

  const handleRetryUpload = async (itemId) => {
    const queueItem = uploadQueue.find((item) => item.id === itemId);
    if (!queueItem) {
      return;
    }

    setError("");
    const succeeded = await uploadQueueItem(queueItem);
    if (succeeded) {
      await loadFiles();
      setSuccessMessage(`Uploaded ${queueItem.file.name}.`);
    }
  };

  const handleDismissQueueItem = (itemId) => {
    setUploadQueue((current) => current.filter((item) => item.id !== itemId));
  };

  const handleCopyLink = async (shareUrl) => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setSuccessMessage("Share link copied to clipboard.");
      setError("");
    } catch (copyError) {
      setError("Unable to copy link to clipboard.");
    }
  };

  const handleRevoke = async (shareToken) => {
    const { error: revokeError } = await revokeSecureFileShare(shareToken);
    if (revokeError) {
      setError(revokeError.message);
      return;
    }
    setSuccessMessage("Share link revoked.");
    await loadFiles();
  };

  const handleRegenerate = async (shareToken) => {
    const { data, error: regenerateError } = await regenerateSecureFileShare(shareToken);
    if (regenerateError) {
      setError(regenerateError.message);
      return;
    }
    setSuccessMessage(`New share link generated. Expires on ${moment(data.shareExpiresAt).format("DD/MM/YYYY")}.`);
    await loadFiles();
  };

  const handleDelete = async (shareToken) => {
    const confirmed = window.confirm("Delete this file permanently?");
    if (!confirmed) {
      return;
    }

    const { error: deleteError } = await deleteSecureFile(shareToken);
    if (deleteError) {
      setError(deleteError.message);
      return;
    }
    setSuccessMessage("File deleted.");
    await loadFiles();
  };

  const handleViewAccessLogs = async (file) => {
    setIsAccessLogsLoading(true);
    setSelectedAccessLogs({ fileName: file.fileName, logs: [] });
    const { data, error: logsError } = await getSecureFileAccessLogs(file.shareToken);
    if (logsError) {
      setError(logsError.message);
      setSelectedAccessLogs(null);
    } else {
      setSelectedAccessLogs({ fileName: file.fileName, logs: data || [] });
    }
    setIsAccessLogsLoading(false);
  };

  const getMoreMenuItems = (file) => [
    { key: "revoke", label: "Revoke Link", disabled: !file.shareActive },
    { key: "regenerate", label: "New Link" },
    { type: "divider" },
    { key: "delete", label: "Delete File", danger: true },
  ];

  const handleMoreMenuClick = async (file, { key }) => {
    switch (key) {
      case "revoke":
        await handleRevoke(file.shareToken);
        break;
      case "regenerate":
        await handleRegenerate(file.shareToken);
        break;
      case "delete":
        await handleDelete(file.shareToken);
        break;
      default:
        break;
    }
  };

  const renderUploadQueueItem = (item) => {
    const statusLabel = {
      [UPLOAD_STATUS.PENDING]: "Waiting",
      [UPLOAD_STATUS.UPLOADING]: "Uploading",
      [UPLOAD_STATUS.SUCCESS]: "Uploaded",
      [UPLOAD_STATUS.ERROR]: "Failed",
    }[item.status];

    return (
      <div key={item.id} className={`secure-file-upload-item secure-file-upload-item--${item.status}`}>
        <div className="secure-file-upload-item-header">
          <span className="secure-file-upload-item-name">{item.file.name}</span>
          <span className="secure-file-upload-item-status">{statusLabel}</span>
        </div>
        {(item.status === UPLOAD_STATUS.UPLOADING || item.status === UPLOAD_STATUS.SUCCESS) && (
          <progress className="secure-file-upload-progress" max="100" value={item.progress} />
        )}
        {item.status === UPLOAD_STATUS.ERROR && <p className="secure-file-upload-item-error">{item.errorMessage}</p>}
        <div className="secure-file-upload-item-actions">
          {item.status === UPLOAD_STATUS.ERROR && (
            <button
              type="button"
              className="button is-small is-info is-light is-radiusless"
              onClick={() => handleRetryUpload(item.id)}
            >
              Retry
            </button>
          )}
          {(item.status === UPLOAD_STATUS.SUCCESS || item.status === UPLOAD_STATUS.ERROR) && (
            <button
              type="button"
              className="button is-small is-light is-radiusless"
              onClick={() => handleDismissQueueItem(item.id)}
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderActions = (file) => (
    <div className="secure-file-actions">
      <button
        type="button"
        className="button is-small is-info is-light is-radiusless"
        disabled={!file.shareActive}
        onClick={() => handleCopyLink(file.shareUrl)}
      >
        Copy Link
      </button>
      <button
        type="button"
        className="button is-small is-light is-radiusless"
        onClick={() => handleViewAccessLogs(file)}
      >
        Audit Log
      </button>
      <Dropdown
        menu={{
          items: getMoreMenuItems(file),
          onClick: (info) => handleMoreMenuClick(file, info),
        }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <button
          type="button"
          className="button is-small is-light is-radiusless secure-file-action-trigger"
          aria-label="More file actions"
          onClick={(event) => event.stopPropagation()}
        >
          More
        </button>
      </Dropdown>
    </div>
  );

  return (
    <div className="box is-shadowless secure-file-tab">
      <div className="level mb-4">
        <div className="level-left">
          <div className="level-item">
            <div>
              <p className="heading has-text-grey">Secure File Storage</p>
              <p className="is-size-7 has-text-grey">
                Files are stored securely and shared via password-protected links (patient DOB as DDMMYYYY).
              </p>
            </div>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <input ref={fileInputRef} type="file" multiple hidden onChange={handleFilesSelected} />
            <button
              type="button"
              className={`button is-info is-small is-radiusless ${isUploading ? "is-loading" : ""}`}
              onClick={handleUploadClick}
              disabled={isUploading}
            >
              Upload Files
            </button>
          </div>
        </div>
      </div>

      {uploadQueue.length > 0 && (
        <div className="secure-file-upload-queue mb-4">
          <p className="is-size-7 has-text-weight-semibold mb-2">Upload progress</p>
          {uploadQueue.map(renderUploadQueueItem)}
        </div>
      )}

      {error && (
        <div className="notification is-danger is-light is-size-7">
          <button className="delete" onClick={() => setError("")} aria-label="dismiss error" />
          {error}
        </div>
      )}

      {successMessage && (
        <div className="notification is-success is-light is-size-7">
          <button className="delete" onClick={() => setSuccessMessage("")} aria-label="dismiss success" />
          {successMessage}
        </div>
      )}

      <div className="secure-file-table-wrapper">
        {!isLoading && files.length > 0 && (
          <SecureFileSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            filteredCount={filteredFiles.length}
            totalCount={files.length}
          />
        )}
        {isLoading ? (
          <div className="has-text-centered py-6 has-text-grey">Loading secure files...</div>
        ) : files.length === 0 ? (
          <div className="has-text-centered py-6 has-text-grey">No secure files uploaded yet.</div>
        ) : filteredFiles.length === 0 ? (
          <div className="has-text-centered py-6 has-text-grey">No files match your search.</div>
        ) : (
          <table className="table is-fullwidth is-hoverable is-striped is-narrow">
            <SecureFileTableHead sortConfig={sortConfig} onSort={handleSort} />
            <tbody className="is-size-7">
              {displayedFiles.map((file) => {
                const status = getShareStatus(file);
                return (
                  <tr key={file.shareToken}>
                    <td>{file.fileName}</td>
                    <td>{file.uploadedByName || "Unknown"}</td>
                    <td className="secure-file-uploaded-at">{formatUploadedAt(file.createdAt)}</td>
                    <td className="secure-file-uploaded-at">{formatRetentionExpiresAt(file.retentionExpiresAt)}</td>
                    <td>{formatFileSize(file.fileSize)}</td>
                    <td>
                      <div className="secure-file-share-status">
                        <span className={status.className}>{status.label}</span>
                        <p className="is-size-7 has-text-grey mt-1 mb-0">{status.linkDetail}</p>
                      </div>
                    </td>
                    <td className="secure-file-actions-column">{renderActions(file)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className={`modal ${selectedAccessLogs ? "is-active" : ""}`}>
        <div className="modal-background" onClick={() => setSelectedAccessLogs(null)} />
        <div className="modal-content">
          <div className="box">
            <h3 className="title is-5">Access Audit Log</h3>
            <p className="is-size-7 has-text-grey mb-4">{selectedAccessLogs?.fileName}</p>
            {isAccessLogsLoading ? (
              <p className="has-text-grey">Loading access logs...</p>
            ) : selectedAccessLogs?.logs?.length ? (
              <table className="table is-fullwidth is-narrow is-size-7">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Type</th>
                    <th>Result</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedAccessLogs.logs.map((log) => (
                    <tr key={log.id}>
                      <td>{displayDateTime(log.accessedAt)}</td>
                      <td>{log.accessType}</td>
                      <td>{log.success ? "Success" : "Failed"}</td>
                      <td>{log.ipAddress || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="has-text-grey">No access attempts recorded yet.</p>
            )}
          </div>
        </div>
        <button className="modal-close is-large" aria-label="close" onClick={() => setSelectedAccessLogs(null)} />
      </div>
    </div>
  );
};

export default SecureFileTab;
